import React, { useState, useEffect, useMemo } from 'react';
import { formatMoney, useCurrency } from '../context/CurrencyContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DatePicker from '../components/DatePicker';
import axios from 'axios';
import { Card } from '../components/ui/card';
import {
  MapPin, Clock, Users, Loader2, BanIcon, Star
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatYen(amount, cur) {
  return formatMoney(amount, cur);
}

// Drapeaux des langues parlées par le guide (affichés sur la carte).
const LANG_FLAG = { fr: '🇫🇷', en: '🇬🇧', jp: '🇯🇵' };

// Langue courante -> clés internes (fr / en / jp).
function langKey(l) {
  l = (l || 'en').toLowerCase();
  if (l.indexOf('ja') === 0 || l.indexOf('jp') === 0) return 'jp';
  if (l.indexOf('fr') === 0) return 'fr';
  return 'en';
}

// Texte multilingue ({fr,en,jp}) dans la langue du visiteur, avec repli.
function pick(obj, lng) {
  if (!obj) return '';
  if (typeof obj === 'string') return obj;
  var k = langKey(lng);
  return obj[k] || obj.fr || obj.en || obj.jp || '';
}

export function ActivityCard({ activity, onClick }) {
  const { t, i18n } = useTranslation();
  const slots = activity.availability?.time_slots || [];
  const labels = [];
  if (slots.includes('morning')) labels.push(t('activities.morning'));
  if (slots.includes('afternoon')) labels.push(t('activities.afternoon'));
  if (slots.includes('evening')) labels.push(t('activities.evening'));
  const slotsLabel = labels.join(' • ') || t('activities.flexible');

  const name = pick(activity.name, i18n.language) || t('activities.activityFallback');
  const desc = pick(activity.description, i18n.language);
  const guideName = activity.guide_name || t('activities.guideFallback');

  const isSej = activity.activity_type === 'sejour';
  const pkg = activity.package_prices || {};
  const minP = parseInt(activity.min_participants, 10) || 1;
  const pkgVals = Object.keys(pkg).filter(function(k) { return (parseInt(k, 10) || 0) >= minP; }).map(function(k) { return parseInt(pkg[k], 10) || 0; }).filter(function(v) { return v > 0; });
  const sejMinPrice = pkgVals.length ? Math.min.apply(null, pkgVals) : 0;
  const destLabel = Array.isArray(activity.destinations) ? activity.destinations.join(' • ') : '';

  return (
    <Card
      className="bg-zinc-900/50 border-white/10 overflow-hidden hover:border-[#F9004D]/50 transition-all cursor-pointer"
      onClick={onClick}
      data-testid={`activity-card-${activity.activity_id}`}
    >
      <div className="h-48 bg-gradient-to-br from-[#F9004D]/20 to-[#00F0FF]/20 relative">
        {activity.photos && activity.photos.length > 0 ? (
          <img src={activity.photos[0]} alt={activity.location} className="absolute inset-0 w-full h-full object-cover"
            onError={function(e) { e.target.style.display='none'; }} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <MapPin className="h-16 w-16 text-white/30" />
          </div>
        )}
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className="px-3 py-1 bg-black/60 backdrop-blur-md rounded-full text-sm text-white">
            {activity.location}
          </span>
          {isSej && activity.duration_days && (
            <span className="px-3 py-1 bg-[#F9004D]/80 backdrop-blur-md rounded-full text-xs text-white font-semibold">
              {t('activities.stayDays', { count: activity.duration_days })}
            </span>
          )}
        </div>
        {activity.total_reviews > 0 && (
          <div className="absolute bottom-4 left-4">
            <span className="px-3 py-1 bg-black/70 backdrop-blur-md rounded-full text-sm text-white flex items-center gap-1" data-testid="activity-rating">
              <Star className="h-3 w-3 fill-[#D4AF37] text-[#D4AF37]" />
              {Number(activity.average_rating).toFixed(1)} ({activity.total_reviews})
            </span>
          </div>
        )}
        {!activity.children_allowed && (
          <div className="absolute top-4 right-4">
            <span className="px-3 py-1 bg-red-500/80 backdrop-blur-md rounded-full text-xs text-white flex items-center gap-1">
              <BanIcon className="h-3 w-3" /> {t('activities.noMinors')}
            </span>
          </div>
        )}
      </div>

      <div className="p-6">
        <h3 className="text-xl font-bold text-white mb-2">
          {name}
        </h3>
        <p className="text-zinc-400 text-sm mb-4 line-clamp-2">
          {desc}
        </p>

        <div className="flex items-center gap-3 mb-4">
          <img
            src={activity.guide_picture || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%2327272a'/%3E%3Ccircle cx='50' cy='40' r='16' fill='%2352525b'/%3E%3Cpath d='M22 82c0-15 12-24 28-24s28 9 28 24' fill='%2352525b'/%3E%3C/svg%3E"}
            alt={guideName}
            className="w-10 h-10 rounded-full object-cover object-top"
          />
          <span className="text-zinc-300">{guideName}</span>
          {Array.isArray(activity.guide_languages) && activity.guide_languages.length > 0 && (
            <span className="ml-auto flex items-center gap-1 text-base" title={activity.guide_languages.join(', ')}>
              {activity.guide_languages.map(function(l) { return <span key={l}>{LANG_FLAG[l]}</span>; })}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 text-sm text-zinc-500 mb-4">
          {isSej ? <MapPin className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
          <span className="line-clamp-1">{isSej ? (destLabel || activity.location) : slotsLabel}</span>
        </div>

        <div className="flex items-center justify-between">
          <div>
            {isSej ? (
              <>
                <span className="text-zinc-500 text-xs block">{t('activities.fromPrice')}</span>
                <span className="text-2xl font-bold text-[#F9004D]">{sejMinPrice ? formatYen(sejMinPrice, activity.currency) : '—'}</span>
              </>
            ) : (
              <>
                <span className="text-2xl font-bold text-[#F9004D]">{formatYen(activity.adult_rate, activity.currency)}</span>
                <span className="text-zinc-500 text-sm">{t('activities.perDayShort')}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-zinc-500" />
            <span className="text-zinc-500 text-sm">{t('activities.max', { count: activity.max_participants })}</span>
          </div>
        </div>

        {!isSej && activity.children_allowed && (
          <div className="mt-2 text-sm text-[#D4AF37]">
            {t('activities.child')}: {formatYen(activity.child_rate, activity.currency)}{t('activities.perDayShort')}
          </div>
        )}
      </div>
    </Card>
  );
}

export default function Activities() {
  useCurrency(); // re-render quand la devise d'affichage change
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [allActivities, setAllActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState('all');
  // Un voyageur cherche d'abord QUAND il vient et A COMBIEN. Le créneau
  // matin/après-midi a été retiré : il n'a aucun sens pour un séjour de
  // plusieurs jours, qu'il écartait à tort des résultats.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [people, setPeople] = useState('');
  const [actType, setActType] = useState('all');


  useEffect(() => {
    let cancelled = false;

    const fetchActivities = async () => {
      setLoading(true);
      try {
        // Le filtrage par dates est fait par le SERVEUR : lui seul connaît les
        // congés du guide, les dates déjà réservées et les préavis.
        const p = new URLSearchParams();
        if (dateFrom) p.set('date_from', dateFrom);
        if (dateFrom && dateTo) p.set('date_to', dateTo);
        if (people) p.set('participants', people);
        if (actType !== 'all') p.set('activity_type', actType);
        const url = API_URL + '/api/activities/public' + (p.toString() ? '?' + p.toString() : '');
        const res = await axios.get(url);
        if (!cancelled) {
          setAllActivities(res.data || []);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(t('activities.loadError'));
          setLoading(false);
        }
      }
    };

    fetchActivities();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, people, actType]);

  // Filter activities by location
  const filteredActivities = useMemo(() => {
    if (selectedLocation === 'all') return allActivities;
    return allActivities.filter(a => a.location === selectedLocation);
  }, [allActivities, selectedLocation]);

  // Build location options
  const locationOptions = useMemo(() => {
    const locations = new Map();
    allActivities.forEach(a => {
      if (a.location) {
        locations.set(a.location, (locations.get(a.location) || 0) + 1);
      }
    });
    const sorted = Array.from(locations.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    return [
      { value: 'all', label: t('activities.allDestinations', { count: allActivities.length }) },
      ...sorted.map(([loc, count]) => ({ value: loc, label: `${loc} (${count})` }))
    ];
  }, [allActivities, t]);

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12" data-testid="activities-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
            {t('activities.title')}
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            {t('activities.subtitle')}
          </p>
        </div>

        <Card className="bg-zinc-900/50 border-white/10 p-6 mb-8">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="text-zinc-400 text-sm mb-2 block">{t('activities.destination')}</label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-white"
                data-testid="location-filter"
              >
                {locationOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <label className="text-zinc-400 text-sm mb-2 block">{t('activities.searchType')}</label>
              <select
                value={actType}
                onChange={(e) => setActType(e.target.value)}
                className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-white"
                data-testid="type-filter"
              >
                <option value="all">{t('activities.typeAll')}</option>
                <option value="journee">{t('activities.typeDay')}</option>
                <option value="sejour">{t('activities.typeStay')}</option>
              </select>
            </div>
            <div className="flex-1 min-w-[130px]">
              <label className="text-zinc-400 text-sm mb-2 block">{t('activities.searchPeople')}</label>
              <select
                value={people}
                onChange={(e) => setPeople(e.target.value)}
                className="w-full bg-black/20 border border-white/10 rounded-lg px-4 py-3 text-white"
                data-testid="people-filter"
              >
                <option value="">{t('activities.anyPeople')}</option>
                {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-white/10">
            <div className="flex-1 min-w-[150px]">
              <label className="text-zinc-400 text-sm mb-2 block">
                {t('activities.searchDates')} — {t('activities.searchFrom')}
              </label>
              <DatePicker
                value={dateFrom}
                onChange={setDateFrom}
                placeholder={t('activities.anyDate')}
                testId="date-from-filter"
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <label className="text-zinc-400 text-sm mb-2 block">{t('activities.searchTo')}</label>
              <DatePicker
                value={dateTo}
                onChange={setDateTo}
                minDate={dateFrom || undefined}
                placeholder={t('activities.anyDate')}
                testId="date-to-filter"
              />
            </div>
            {(dateFrom || dateTo || people || actType !== 'all' || selectedLocation !== 'all') && (
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => { setDateFrom(''); setDateTo(''); setPeople(''); setActType('all'); setSelectedLocation('all'); }}
                  className="px-4 py-3 rounded-lg border border-white/20 text-zinc-300 hover:text-white hover:border-[#F9004D]/60"
                >
                  {t('activities.resetFilters')}
                </button>
              </div>
            )}
          </div>
          {dateFrom && <p className="text-zinc-500 text-xs mt-3">{t('activities.datesHint')}</p>}
        </Card>

        <div className="mb-6">
          <p className="text-zinc-400">
            {t('activities.count', { count: filteredActivities.length })}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-12 w-12 text-[#F9004D] animate-spin" />
          </div>
        ) : filteredActivities.length === 0 ? (
          <Card className="bg-zinc-900/50 border-white/10 p-12 text-center">
            <MapPin className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">{t('activities.none')}</h3>
            <p className="text-zinc-400">{t('activities.noneDesc')}</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredActivities.map(activity => (
              <ActivityCard
                key={activity.activity_id}
                activity={activity}
                onClick={() => navigate('/activities/' + activity.activity_id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
